import { AFSCHERPEN_PP_FACTOR, grindFamilyForSlug, grindKeyFor, ppAreaFactor, ppPerMeterCents, ppShapeFactor, isAlwaysArrised, requiresGroundEdge, facetBandenVoorDikte, facetMogelijk, FACET_BAND_EDGE_ID, FACET_BAND_RANGE, FACET_TARIEVEN } from "@glastotaal/core";
import type { Cents, EdgeFinish, GlassVariant } from "@glastotaal/core";

export interface EdgeFinishOption {
  id: string;
  label: string;
  perM: Cents;
  /** Bovengrens van de facetband (mm); alleen bij facet-afwerkingen — stuurt ook de tekening. */
  facetMm?: number;
  /** Ondergrens van de facetband (mm) — smaller bestaat niet in het prijzenboek. */
  facetMinMm?: number;
  /** Tonen maar nog niet kiesbaar (prijs onbekend) → "Binnenkort"-badge. */
  comingSoon?: boolean;
}

// Afscherpen ("scherp afhalen" — de scherpe glaskant licht breken). Voorlopige prijs =
// 50% van het PP-slijpen (platpoly), tot de echte inkoopprijs bekend is. Zonder polijst-
// prijs valt 'ie terug op "Binnenkort" (comingSoon, nog niet kiesbaar).
// Herexport: de factor zelf staat in core, zodat de engine dezelfde helft rekent.
export { AFSCHERPEN_PP_FACTOR } from "@glastotaal/core";
const AFSCHERPEN: EdgeFinishOption = { id: "afscherpen", label: "Afscherpen", perM: 0 as Cents, comingSoon: true };

// Verstek 45° is tijdelijk AFGESCHERMD (klopt nog niet goed). Zet op true zodra het klopt.
const VERSTEK_ENABLED = false;

/**
 * Edge finishes Timmers offers for this variant (Platpoly / C-slijp / verstek).
 *
 * `shapeId` is de gekozen vorm: op een model (schuine zijde, toog, cirkel, ovaal) kost slijpen
 * 100% meer — bij gelaagd 50% voor een schuine zijde. `areaM2` is het glasoppervlak: boven 3 m²
 * kost slijpen 50% meer, boven 6 m² 100% en boven 10 m² 150%. Beide zitten al in de prijs die de
 * engine rekent, maar het tarief op de keuzekaart liet ze niet zien — die bleef €17,00/m1 tonen
 * terwijl er €25,50 gerekend werd. Nu volgt het bedrag op de kaart dezelfde factoren.
 */
export function edgeFinishOptions(variant: GlassVariant, shapeId?: string, areaM2 = 0): EdgeFinishOption[] {
  // Dun glas (2/3 mm) is te breekbaar om te slijpen → geen randafwerking (alleen "Geen").
  if ((variant.thicknessMm ?? 0) <= 3) return [];
  // Beloopbaar glas wordt áltijd rondom PP-geslepen (in de Timmers-prijs inbegrepen) → dat is de
  //   enige (voorgeselecteerde) optie; geen andere randafwerking te kiezen, geen toeslag.
  if (/beloopbaar/i.test(variant.subcategory ?? "")) {
    return [{ id: "polijst", label: "PP-geslepen (rondom gepolijst) — inbegrepen", perM: 0 as Cents }];
  }
  let base = variant.ops?.edgeFinishes?.length
    ? variant.ops.edgeFinishes
    : variant.ops?.slijpPerM != null
      ? [{ id: "polijst", label: "Platpoly gepolijst (PP)", perM: variant.ops.slijpPerM }]
      : [];
  // C-slijp (afgeronde rand) vervalt voorlopig; verstek 45° staat ook uit tot 'ie klopt.
  base = base.filter((o) => o.id !== "cslijp" && (VERSTEK_ENABLED || o.id !== "verstek"));
  // Figuurglas heeft een ingewalst (structuur)patroon → een facet kun je er niet op slijpen.
  const isFiguur = /figuur/i.test(variant.subcategory ?? "");
  if (isFiguur) base = base.filter((o) => o.facetMm == null);
  // Tarieven uit de werkbladen i.p.v. de (te lage) sync-prijs, zodat het label op de knop
  // hetzelfde bedrag toont als de engine rekent. Alleen PP en facet staan in de werkbladen;
  // verstek/C-slijp houden hun catalogusprijs.
  const family = grindFamilyForSlug(`${variant.subcategory ?? ""} ${variant.name ?? ""}`);
  const dikte = variant.thicknessMm ?? 0;
  const slijpKey = grindKeyFor(family, variant);
  const ppPerM = ppPerMeterCents(family, slijpKey);
  base = base.map((o) => (o.id === "polijst" && ppPerM > 0 ? { ...o, perM: ppPerM as Cents } : o));
  if (family === "enkel") {
    // Facetbanden komen uit het werkblad, niet uit de catalogus: die kent alleen 30/40/50 mm,
    // waardoor de 10–25-band van 4 mm glas nergens te kiezen was — terwijl er wél een prijs
    // voor staat. Andersom bood de catalogus banden aan die voor die dikte geen prijs hebben.
    // facetMogelijk vangt de modellen af: op een toog/cirkel kan een facet pas vanaf 6 mm.
    const facetKan = !isFiguur && facetMogelijk(dikte, shapeId);
    base = [...base.filter((o) => o.facetMm == null), ...(facetKan ? facetOptionsForThickness(dikte, ppPerM) : [])];
  }

  // Vaste weergavenaam voor polijst (ongeacht wat de data-transform meegaf).
  base = base.map((o) => (o.id === "polijst" ? { ...o, label: "Platpoly gepolijst (PP)" } : o));
  // Geharde glassoorten (ESG) worden vóór het harden standaard afgescherpt → altijd inbegrepen,
  // zonder toeslag. De basiskaart heet daar "Afgescherpt · inbegrepen" (alwaysArrised) i.p.v.
  // "Geen randafwerking / scherp"; een losse "Afscherpen"-optie zou dat dubbelen → weglaten.
  // Lacobel-T/Matelac-T zijn getoughened (ESG) → ook hier standaard afgescherpt. Installatieglas
  // (deurglas) is eveneens gehard. Zelfde bron als de UI-vlag: isAlwaysArrised in core.
  const isGehard = isAlwaysArrised(variant);
  if (isGehard) return withShapeFactor(base, family, shapeId, areaM2);
  // Vanaf 12 mm moet de rand vlak geslepen zijn (prijslijst pag. 5): alleen de snijkant breken
  // is dan niet genoeg, dus afscherpen vervalt als keuze. Hier gefilterd i.p.v. in de UI, zodat
  // ook de prijs-fallback ("eerste optie") nooit op een verboden afwerking uitkomt.
  if (requiresGroundEdge(variant.thicknessMm ?? 0)) return withShapeFactor(base, family, shapeId, areaM2);
  // Afscherpen voor niet-gehard glas: voorlopige prijs = 50% van het PP-slijpen.
  const polijstPerM = base.find((o) => o.id === "polijst")?.perM ?? variant.ops?.slijpPerM ?? 0;
  const afscherpen: EdgeFinishOption = polijstPerM > 0
    ? { id: "afscherpen", label: "Afscherpen", perM: Math.round(polijstPerM * AFSCHERPEN_PP_FACTOR) as Cents }
    : AFSCHERPEN;
  // Volgorde: Afscherpen vóór Platpoly gepolijst (Geen → Afscherpen → PP → Facet → Facet+PP).
  return withShapeFactor([afscherpen, ...base], family, shapeId, areaM2);
}

/** Vorm- én oppervlaktetoeslag over de getoonde m1-tarieven, precies zoals de engine ze rekent. */
function withShapeFactor(
  opts: EdgeFinishOption[],
  family: ReturnType<typeof grindFamilyForSlug>,
  shapeId?: string,
  areaM2 = 0,
): EdgeFinishOption[] {
  const factor = ppShapeFactor(family, shapeId) * ppAreaFactor(areaM2, family);
  if (factor === 1) return opts;
  return opts.map((o) => (o.perM > 0 ? { ...o, perM: Math.round(o.perM * factor) as Cents } : o));
}

/** An EdgeFinish for the engine; Timmers charges a 1 m¹ minimum (the finish min price). */
export function buildEdgeFinish(opt: EdgeFinishOption): EdgeFinish {
  return { id: opt.id, name: opt.label, pricePerMeter: opt.perM, minPrice: Math.round(opt.perM), facetMm: opt.facetMm };
}


/**
 * De facet-keuzes voor een glasdikte, rechtstreeks uit het werkblad: één kaart per breedteband
 * die voor die dikte een prijs heeft, plus de "+ PP"-variant. `facetMm`/`facetMinMm` dragen de
 * grenzen van de band, zodat de schuifbalk niet buiten een geprijsde breedte kan komen.
 */
function facetOptionsForThickness(thicknessMm: number, ppPerM: number): EdgeFinishOption[] {
  const out: EdgeFinishOption[] = [];
  for (const band of facetBandenVoorDikte(thicknessMm)) {
    const perM = FACET_TARIEVEN[band][thicknessMm];
    if (perM == null || perM <= 0) continue;
    const { minMm, maxMm } = FACET_BAND_RANGE[band];
    const id = FACET_BAND_EDGE_ID[band];
    const label = `Facet ${minMm}–${maxMm} mm`;
    out.push({ id, label, perM: perM as Cents, facetMm: maxMm, facetMinMm: minMm });
    if (ppPerM > 0) {
      out.push({ id: `${id}+pp`, label: `${label} + PP`, perM: (perM + ppPerM) as Cents, facetMm: maxMm, facetMinMm: minMm });
    }
  }
  return out;
}
